<template>
	<div class="relative z-10">
		<div
			class="counter absolute top-0 right-0 bg-error-500 rounded-1 pointer-events-none d-flex"
			v-if="unreadCount"
		>
			<span class="text-label-xs text-neutral-25 font-weight-bold">{{ unreadCount }}</span>
		</div>
		<Dropdown :gap="isXS ? 4 : 16" placement="end" @opened="getNotificationsOnDropdownOpen">
			<template #trigger="{ toggle, isActive }">
				<PButton
					:type="isActive ? 'filled' : 'text'"
					:color="isActive ? 'reverse' : 'neutral'"
					@click="toggle"
				>
					<Icon class="text-primary-800" :icon="isActive ? 'bell-filled' : 'bell'" radius="22px" />
				</PButton>
			</template>
			<template #default="{ close }">
				<ScrollPagination
					class="vertical-scroll text-neutral-950"
					:class="isXS ? 'notifications-container-mobile' : 'notifications-container'"
					@end-of-scroll="loadNextPage"
				>
					<div
						class="d-flex align-center justify-space-between pa-4 border-b-xs border-neutral-100"
					>
						<p class="text-title-m">{{ $t('web.home.notifications.title') }}</p>
						<PButton
							v-if="unreadCount"
							type="text"
							color="social"
							size="sm"
							@click="onMarkAllAsRead"
						>
							{{ $t('web.home.notifications.mark_all_as_read') }}
						</PButton>
					</div>
					<div class="pa-4 d-flex flex-column gap-2">
						<template v-if="notifications.length">
							<div v-for="(dayNotifications, day, i) of notificationsByDay" :key="i">
								<p class="text-neutral-800 text-title-s pb-2">{{ day }}</p>
								<div class="d-flex flex-column gap-2">
									<div
										v-for="notification in dayNotifications"
										:key="notification.id!"
										class="notification-box width-full rounded-2 d-flex gap-1 align-center px-3 py-2 cursor-pointer"
										@click="(close(), onNotificationClick(notification))"
									>
										<div class="flex-grow-1 d-flex gap-2">
											<Cover
												height="32px"
												width="32px"
												:src="notification.cover || ''"
												class="rounded-2"
											/>
											<div class="flex-grow-1">
												<p class="text-body-m text-neutral-950 pb-1">{{ notification.message }}</p>
												<p class="text-body-s text-neutral-800">
													{{ formatHour(notification.created) }}
												</p>
											</div>
										</div>
										<div
											v-if="notification.status === 'unread'"
											class="unread-dot flex-shrink-0 bg-error-500 rounded-circle"
										></div>
									</div>
								</div>
							</div>
						</template>
						<HeaderNotificationsEmptyState
							v-if="!notifications.length && !isLoading"
							class="mt-20"
						/>
						<Skeleton v-if="isLoading" class="width-full rounded-2" style="height: 58px" />
					</div>
				</ScrollPagination>
			</template>
		</Dropdown>
	</div>
</template>

<script setup lang="ts">
	import { ref, onMounted, computed } from 'vue'
	import { NotificationActions } from '~/actions/notifications/action'
	import { Notification, NOTIFICATION_STATUS, NOTIFICATION_TYPE } from '~/models/Notification'
	import Calendar from '~/passporter-services/shared/calendar'
	import { TripNavigation } from '~/services/navigation/trip'
	import { WallPinActions } from '~/actions/trip/wallPin'
	import { MyTripsNavigation } from '~/services/navigation/myTrips'

	interface Props {
		userId?: string
		isXS: boolean
	}
	const props = defineProps<Props>()
	const { locale } = useI18n()

	const calendar = new Calendar(locale.value)
	const unreadCount = ref<number>()
	const notifications = ref<Notification[]>([])
	const nextPage = ref<number>()
	const isLoading = ref<boolean>()

	onMounted(() => {
		getUnreadCount()
	})

	const getUnreadCount = async () => {
		if (!props.userId) return
		unreadCount.value = await NotificationActions.countUnread(props.userId)
	}

	const getNotifications = async (page: number = 0) => {
		if (!props.userId) return
		isLoading.value = true
		nextPage.value = undefined
		const notificationsPage = await NotificationActions.getByUser(props.userId, page)
		notifications.value = [...notifications.value, ...notificationsPage.items]
		nextPage.value = notificationsPage.next
		isLoading.value = false
	}

	const getNotificationsOnDropdownOpen = () => {
		if (notifications.value.length) return
		const displayLoader: boolean = true
		resetNotifications(displayLoader)
	}

	const resetNotifications = async (displayLoader: boolean = false) => {
		nextPage.value = undefined
		if (displayLoader) isLoading.value = true
		const notificationsPage = await NotificationActions.getByUser(props.userId!)
		notifications.value = notificationsPage.items
		nextPage.value = notificationsPage.next
		isLoading.value = false
	}

	const loadNextPage = () => {
		if (!nextPage.value) return
		getNotifications(nextPage.value)
	}

	const notificationsByDay = computed(() => {
		if (!notifications.value) return
		return NotificationActions.formatByDay(notifications.value, locale as unknown as string)
	})

	const navigate = (notification: Notification, go: (itineraryId: string) => void) => {
		markAsRead(notification)
		const itineraryId = notification.data?.itineraryId
		if (!itineraryId) return
		go(itineraryId)
		getUnreadCount()
		resetNotifications()
	}

	const onNotificationClick = (notification: Notification) => {
		const actionMap: Record<NOTIFICATION_TYPE, () => void> = {
			[NOTIFICATION_TYPE.TASK_ASSIGNED]: () =>
				navigate(notification, (id) => TripNavigation.toTripTasks(id)),
			[NOTIFICATION_TYPE.TRIP_DELETED]: () => toTripList(notification),
			[NOTIFICATION_TYPE.TRIP_DATE_CHANGED]: () =>
				navigate(notification, (id) => TripNavigation.toTripDashboard(id)),
			[NOTIFICATION_TYPE.TRIP_TITLE_CHANGED]: () =>
				navigate(notification, (id) => TripNavigation.toTripDashboard(id)),
			[NOTIFICATION_TYPE.TRIP_INVITATION_ACCEPTED]: () =>
				navigate(notification, (id) => TripNavigation.toTripDashboard(id)),
			[NOTIFICATION_TYPE.TRIP_INVITATION_RECEIVED]: () => toTripInvitation(notification),
			[NOTIFICATION_TYPE.WALL_PIN_CREATED]: () =>
				navigate(notification, (id) => {
					WallPinActions.clearWallPins(id)
					TripNavigation.toTripWall(id)
				}),
			[NOTIFICATION_TYPE.WALL_PIN_DELETED]: () =>
				navigate(notification, (id) => {
					WallPinActions.clearWallPins(id)
					TripNavigation.toTripWall(id)
				}),
			[NOTIFICATION_TYPE.BOOKING_CREATED]: () =>
				navigate(notification, (id) => TripNavigation.toTripDocuments(id)),
			[NOTIFICATION_TYPE.SAVED_SPOT]: () =>
				navigate(notification, (id) => TripNavigation.toTripSpots(id)),
		}
		const action = actionMap[notification.type]
		if (action) action()
	}

	const toTripList = (notification: Notification) => {
		markAsRead(notification)
		MyTripsNavigation.go()
		getUnreadCount()
		resetNotifications()
	}

	const markAsRead = async (notification: Notification) => {
		if (notification.status === NOTIFICATION_STATUS.READ) return
		await NotificationActions.markAsRead(notification.id!)
	}

	const onMarkAllAsRead = async () => {
		await NotificationActions.markAllAsRead()
		notifications.value = notifications.value.map((notification) => ({
			...notification,
			status: NOTIFICATION_STATUS.READ,
		}))
		unreadCount.value = 0
	}

	const formatHour = (date: Date) => {
		return calendar.formatTimeFromDate(date)
	}

	function toTripInvitation(notification: Notification) {
		markAsRead(notification)
		const invitationUrl = notification.data?.invitationUrl
		if (!invitationUrl) return
		TripNavigation.toTripInvitation(invitationUrl)
		getUnreadCount()
		resetNotifications()
	}
</script>

<style scoped lang="scss">
	.counter {
		height: 14px;
		padding: 0 4px;
		margin: 4px 8px;
	}
	.notifications-container {
		max-height: 400px;
		min-height: 400px;
		width: 350px;
	}

	.notifications-container-mobile {
		height: calc(100dvh - 200px);
		width: 90dvw;
	}

	.unread-dot {
		height: 6px;
		width: 6px;
	}

	.notification-box {
		&:hover {
			background-color: var(--color-neutral-100);
		}
	}
</style>
